import {
  AudioDelay,
  ControllerType,
  GameSettings,
} from '@/common/types/student';
import {
  CardsIcon,
  ClockIcon,
  EarIcon,
  HandGrabbingIcon,
  HashIcon,
  JoystickIcon,
  KeyboardIcon,
  MouseLeftClickIcon,
  SmileyIcon,
  SwordIcon,
  UserSoundIcon,
} from '@phosphor-icons/react';
import { Input } from '@shadcn/components/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@shadcn/components/select';
import { Switch } from '@shadcn/components/switch';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

interface LearnerSettingsProps {
  settings: GameSettings;
  onPlayPhonemeChanged: (value: boolean) => void;
  onControllerTypeChange: (value: ControllerType) => void;
  onDuelsEnabledChange: (value: boolean) => void;
  onAudioDelayChange: (value: AudioDelay) => void;
  onDistractorCardsChange: (value: boolean) => void;
  onWordLimitChange: (value: number) => void;
  onWordSegmentationChange: (value: boolean) => void;
  onArticulatoryGesturesChange: (value: boolean) => void;
  largeLabels?: boolean;
  disabled?: boolean;
}
export default function LearnerSettings({
  settings,
  onPlayPhonemeChanged,
  onControllerTypeChange,
  onDuelsEnabledChange,
  onAudioDelayChange,
  onDistractorCardsChange,
  onWordLimitChange,
  onWordSegmentationChange,
  onArticulatoryGesturesChange,
  largeLabels = false,
  disabled = false,
}: LearnerSettingsProps) {
  return (
    <fieldset disabled={disabled} className="contents">
      <PhonemeAudioSetting
        value={settings.playPhonemeOnCardDrop}
        onChange={onPlayPhonemeChanged}
        largeLabels={largeLabels}
      />
      <DistractorCardsSetting
        value={settings.distractorCardsEnabled ?? true}
        onChange={onDistractorCardsChange}
        largeLabels={largeLabels}
      />
      <WordSegmentationSetting
        value={settings.wordSegmentationEnabled}
        onChange={onWordSegmentationChange}
        largeLabels={largeLabels}
      />
      <AudioDelaySetting
        value={settings.audio.audioDelay}
        onChange={(value) => onAudioDelayChange(value)}
        disabled={!settings.wordSegmentationEnabled}
        largeLabels={largeLabels}
      />
      <WordLimitSetting
        value={settings.wordLimit}
        onChange={onWordLimitChange}
        largeLabels={largeLabels}
      />
      <DuelsSetting
        value={settings.duelsEnabled ?? true}
        onChange={onDuelsEnabledChange}
        largeLabels={largeLabels}
      />
      <ControllerTypeSetting
        value={settings.controllerType}
        onChange={onControllerTypeChange}
        largeLabels={largeLabels}
      />
      <ArticulatoryGesturesSetting
        value={settings.articulatoryGesturesEnabled}
        onChange={onArticulatoryGesturesChange}
        largeLabels={largeLabels}
      />
    </fieldset>
  );
}

interface BaseSettingProps {
  largeLabels?: boolean;
}

interface WordLimitSettingProps extends BaseSettingProps {
  value: number;
  onChange: (value: number) => void;
}
const WordLimitSetting = ({
  value,
  onChange,
  largeLabels = false,
}: WordLimitSettingProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [updatedValue, setUpdatedValue] = useState(value.toString());
  const min = 1;
  const max = 20;

  useEffect(() => {
    setUpdatedValue(value.toString());
  }, [value]);

  const applyValue = () => {
    const parsed = Number(updatedValue);
    if (!isNaN(parsed)) {
      const clamped = Math.min(max, Math.max(min, parsed));
      if (clamped !== value) {
        onChange(clamped);
      } else {
        // Reset to prop in case of no change but formatting mismatch
        setUpdatedValue(clamped.toString());
      }
    } else {
      // NaN input → reset to current prop value
      setUpdatedValue(value.toString());
    }
  };

  return (
    <SettingsRow>
      <SettingLabelContainer>
        <SettingLabel $largeLabels={largeLabels}>
          <HashIcon size={16} />
          Word Limit
        </SettingLabel>
        <SettingExplainer>
          Set the number of questions presented in a minigame.
        </SettingExplainer>
      </SettingLabelContainer>
      <Input
        ref={inputRef}
        className="mr-2 self-start"
        type="number"
        min={min}
        max={max}
        value={updatedValue}
        onChange={(e) => {
          setUpdatedValue(e.target.value);
        }}
        onBlur={applyValue}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            inputRef.current?.blur();
          }
        }}
      />
    </SettingsRow>
  );
};

interface PhonemeAudioSettingProps extends BaseSettingProps {
  value: boolean;
  onChange: (value: boolean) => void;
}
const PhonemeAudioSetting = ({
  value,
  onChange,
  largeLabels = false,
}: PhonemeAudioSettingProps) => (
  <SettingsRow>
    <SettingLabelContainer>
      <SettingLabel $largeLabels={largeLabels}>
        <EarIcon size={16} />
        Play phoneme on card drop
      </SettingLabel>
      <SettingExplainer>
        When learner places a card, sound out that card's phoneme or morpheme.
      </SettingExplainer>
    </SettingLabelContainer>

    <Switch
      className="ml-2 self-start"
      checked={value}
      onCheckedChange={onChange}
    />
  </SettingsRow>
);

interface ControllerTypeSettingProps extends BaseSettingProps {
  value: ControllerType;
  onChange: (value: ControllerType) => void;
}
const ControllerTypeSetting = ({
  value,
  onChange,
  largeLabels = false,
}: ControllerTypeSettingProps) => (
  <SettingsRow>
    <SettingLabelContainer>
      <SettingLabel $largeLabels={largeLabels}>
        <JoystickIcon size={16} />
        Controller Type
      </SettingLabel>
      <SettingExplainer>
        Choose how to interact with cards during games. This can be changed
        in-game.
      </SettingExplainer>
    </SettingLabelContainer>

    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="mt-1 mr-2">
        <SelectValue
          placeholder={value.charAt(0).toUpperCase() + value.slice(1)}
        />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ControllerType.DRAG}>
          <ControllerOptionWrapper>
            <HandGrabbingIcon size={16} />
            <span>Drag and Drop</span>
          </ControllerOptionWrapper>
        </SelectItem>
        <SelectItem value={ControllerType.CLICK}>
          <ControllerOptionWrapper>
            <MouseLeftClickIcon size={16} />
            <span>Click to Place</span>
          </ControllerOptionWrapper>
        </SelectItem>
        <SelectItem value={ControllerType.KEYBOARD}>
          <ControllerOptionWrapper>
            <KeyboardIcon size={16} />
            <span>Keyboard</span>
          </ControllerOptionWrapper>
        </SelectItem>
      </SelectContent>
    </Select>
  </SettingsRow>
);

interface DuelsSettingProps extends BaseSettingProps {
  value: boolean;
  onChange: (value: boolean) => void;
}
const DuelsSetting = ({
  value,
  onChange,
  largeLabels = false,
}: DuelsSettingProps) => (
  <SettingsRow>
    <SettingLabelContainer>
      <SettingLabel $largeLabels={largeLabels}>
        <SwordIcon size={16} />
        Enable Spellcasting Duels
      </SettingLabel>
      <SettingExplainer>
        Allow this student to participate in spelling based duels with the
        computer.
      </SettingExplainer>
    </SettingLabelContainer>

    <Switch
      className="ml-2 self-start"
      checked={value}
      onCheckedChange={onChange}
    />
  </SettingsRow>
);

interface AudioDelaySettingProps extends BaseSettingProps {
  value: AudioDelay;
  onChange: (value: AudioDelay) => void;
  disabled?: boolean;
}
const AudioDelaySetting = ({
  value,
  onChange,
  disabled = false,
  largeLabels = false,
}: AudioDelaySettingProps) => {
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    switch (value) {
      case AudioDelay.SHORT:
      case AudioDelay.MEDIUM:
      case AudioDelay.LONG:
        break;
      default:
        onChangeRef.current(AudioDelay.SHORT);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const applyValue = (selected: string) => {
    const parsed = Number(selected);
    switch (parsed) {
      case AudioDelay.SHORT:
      case AudioDelay.MEDIUM:
      case AudioDelay.LONG:
        onChange(parsed);
        break;
      default:
        onChange(AudioDelay.SHORT);
    }
  };

  const getValueLabel = (value: AudioDelay) => {
    switch (value) {
      case AudioDelay.SHORT:
        return 'Short';
      case AudioDelay.MEDIUM:
        return 'Medium ';
      case AudioDelay.LONG:
        return 'Long';
    }
  };

  return (
    <SettingsRow>
      <SettingLabelContainer>
        <SettingLabel $largeLabels={largeLabels} $disabled={disabled}>
          <ClockIcon size={16} />
          Audio Delay
        </SettingLabel>
        <SettingExplainer $disabled={disabled}>
          {disabled
            ? 'Audio delay is disabled when segmented feedback is turned off.'
            : 'Adds a delay when playing segmented feedback. Can help students who need additional auditory processing time.'}
        </SettingExplainer>
      </SettingLabelContainer>
      <div className="flex items-center gap-2 self-start">
        <Select
          value={value.toString()}
          onValueChange={applyValue}
          disabled={disabled}
        >
          <SelectTrigger className="mt-1 mr-2">
            <SelectValue placeholder={getValueLabel(value)} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={AudioDelay.SHORT.toString()}>
              {getValueLabel(AudioDelay.SHORT)}
            </SelectItem>
            <SelectItem value={AudioDelay.MEDIUM.toString()}>
              {getValueLabel(AudioDelay.MEDIUM)}
            </SelectItem>
            <SelectItem value={AudioDelay.LONG.toString()}>
              {getValueLabel(AudioDelay.LONG)}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>
    </SettingsRow>
  );
};

interface DistractorCardsSettingProps extends BaseSettingProps {
  value: boolean;
  onChange: (value: boolean) => void;
}
const DistractorCardsSetting = ({
  value,
  onChange,
  largeLabels = false,
}: DistractorCardsSettingProps) => (
  <SettingsRow>
    <SettingLabelContainer>
      <SettingLabel $largeLabels={largeLabels}>
        <CardsIcon size={16} />
        Enable Distractor Cards
      </SettingLabel>
      <SettingExplainer>
        Include additional cards that aren't part of the target word (increases
        use of working memory)
      </SettingExplainer>
    </SettingLabelContainer>

    <Switch
      className="ml-2 self-start"
      checked={value}
      onCheckedChange={onChange}
    />
  </SettingsRow>
);

interface WordSegmentationSettingProps extends BaseSettingProps {
  value: boolean;
  onChange: (value: boolean) => void;
}
const WordSegmentationSetting = ({
  value,
  onChange,
  largeLabels = false,
}: WordSegmentationSettingProps) => (
  <SettingsRow>
    <SettingLabelContainer>
      <SettingLabel $largeLabels={largeLabels}>
        <UserSoundIcon size={16} />
        Word & Sentence Segmentation
      </SettingLabel>
      <SettingExplainer>
        Segment words and sentences to increase phonological reinforcement.
      </SettingExplainer>
    </SettingLabelContainer>

    <Switch
      className="ml-2 self-start"
      checked={value}
      onCheckedChange={onChange}
    />
  </SettingsRow>
);

interface ArticulatoryGesturesSettingProps extends BaseSettingProps {
  value: boolean;
  onChange: (value: boolean) => void;
}
const ArticulatoryGesturesSetting = ({
  value,
  onChange,
  largeLabels = false,
}: ArticulatoryGesturesSettingProps) => (
  <SettingsRow>
    <SettingLabelContainer>
      <SettingLabel $largeLabels={largeLabels}>
        <SmileyIcon size={16} />
        Articulatory Gestures
      </SettingLabel>
      <SettingExplainer>
        Display mouth shapes for sounds as they are being spoken.
      </SettingExplainer>
    </SettingLabelContainer>

    <Switch
      className="ml-2 self-start"
      checked={value}
      onCheckedChange={onChange}
    />
  </SettingsRow>
);

const SettingsRow = styled.div`
  display: grid;
  grid-template-columns: 5fr 3fr;
  margin-right: 8px;
  min-height: 64px;
  gap: 8px;
`;

const SettingLabelContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SettingLabel = styled.div<{
  $largeLabels?: boolean;
  $disabled?: boolean;
}>`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${({ $disabled }) =>
    $disabled
      ? 'hsl(var(--muted-foreground) / 0.5)'
      : 'hsl(var(--muted-foreground))'};
  font-size: ${({ $largeLabels }) => ($largeLabels ? '14px' : '12px')};
  font-weight: 500;
`;

const SettingExplainer = styled.div<{ $disabled?: boolean }>`
  font-size: 12px;
  color: ${({ $disabled }) =>
    $disabled
      ? 'hsl(var(--muted-foreground) / 0.5)'
      : 'hsl(var(--muted-foreground))'};
`;

const ControllerOptionWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 2px 4px;
`;
